import { useState, useMemo, useCallback } from 'react';
import { Calendar, luxonLocalizer } from 'react-big-calendar';
import { DateTime, Settings } from 'luxon';
import { useExpertTipTopicSchedules } from '../hooks/useExpertTipTopicSchedules';
import {
  transformToTopicCalendarEvent,
  getWeekStartFromDate,
  getUpcomingWeekStarts,
  type TopicCalendarEvent,
} from '../utils/topicScheduleHelpers';
import { getYearMonth } from '../utils/dateHelpers';
import TopicAssignmentModal from '../components/topic-schedule/TopicAssignmentModal';
import TopicCalendarEventComponent from '../components/topic-schedule/TopicCalendarEventComponent';

import 'react-big-calendar/lib/css/react-big-calendar.css';

Settings.defaultZone = 'Europe/London';
const localizer = luxonLocalizer(DateTime, { firstDayOfWeek: 1 });

export default function ExpertTipTopicSchedulePage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedWeekStart, setSelectedWeekStart] = useState<string | null>(null);

  const { year, month } = getYearMonth(currentDate);
  const { data } = useExpertTipTopicSchedules({ year, month });

  const events = useMemo(() => {
    if (!data?.items) return [];
    return data.items.map(transformToTopicCalendarEvent);
  }, [data]);

  const upcomingWeekStarts = useMemo(() => getUpcomingWeekStarts(4), []);
  const scheduledWeekStarts = useMemo(
    () => new Set(data?.items.map((s) => s.weekStart) ?? []),
    [data]
  );

  const handleSelectSlot = useCallback((slotInfo: { start: Date }) => {
    const weekStart = getWeekStartFromDate(slotInfo.start);
    setSelectedWeekStart(weekStart);
    setModalOpen(true);
  }, []);

  const handleSelectEvent = useCallback((event: TopicCalendarEvent) => {
    setSelectedWeekStart(event.resource.weekStart);
    setModalOpen(true);
  }, []);

  const dayPropGetter = useCallback(
    (date: Date) => {
      const weekStart = getWeekStartFromDate(date);
      const isUpcoming = upcomingWeekStarts.includes(weekStart);
      const isUnscheduled = !scheduledWeekStarts.has(weekStart);

      if (isUpcoming && isUnscheduled) {
        return {
          style: {
            border: '1px dashed #94a3b8',
            backgroundColor: '#f8fafc',
          },
        };
      }
      return {};
    },
    [upcomingWeekStarts, scheduledWeekStarts]
  );

  return (
    <div className="space-y-6">
      <h1 className="text-heading-1 text-admin-slate-400">Topic Schedule</h1>

      <div style={{ height: 700 }}>
        <Calendar
          localizer={localizer}
          events={events}
          date={currentDate}
          onNavigate={setCurrentDate}
          view="month"
          views={['month']}
          selectable
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          dayPropGetter={dayPropGetter}
          components={{
            event: TopicCalendarEventComponent,
          }}
          popup={false}
          toolbar
          style={{ height: '100%' }}
        />
      </div>

      {modalOpen && selectedWeekStart && (
        <TopicAssignmentModal
          weekStart={selectedWeekStart}
          onClose={() => {
            setModalOpen(false);
            setSelectedWeekStart(null);
          }}
        />
      )}
    </div>
  );
}
